import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { TextField, FormControlLabel, Checkbox, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Icon from '../../../../atoms/Icon'
import StartForm from './components/ActionForms/StartForm'
import InvokeLambdaForm from './components/ActionForms/InvokeLambdaForm'
import SlackForm from './components/ActionForms/SlackForm'

const ACTION_OPTIONS = [
  { value: 'start', label: 'Start' },
  { value: 'invokeLambda', label: 'Invoke Lambda' },
  { value: 'slack', label: 'Slack' },
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'workflow', label: 'Workflow' }
]

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 0',
    margin: 0
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600
  },
  body: {
    padding: '12px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  divider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    margin: '8px 0'
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#424242',
    margin: '4px 0 0'
  },
  jsonEditor: {
    fontFamily: 'monospace',
    fontSize: 12
  }
})

function StepEditorSidebar ({ step, steps, isNew, onUpdate, onClose }) {
  const classes = useStyles()

  const handleField = useCallback((field, value) => {
    onUpdate({ dagStepId: step.dagStepId, [field]: value })
  }, [step.dagStepId, onUpdate])

  const handleActionChange = useCallback((e) => {
    const action = e.target.value
    const updates = { dagStepId: step.dagStepId, targetAction: action }
    if (action !== 'invokeLambda') {
      updates.isAsync = false
      updates.completeOn = null
      updates.failOn = null
    }
    if (action !== 'invokeLambda' && action !== 'slack') {
      updates.targetIdentifier = null
    }
    onUpdate(updates)
  }, [step.dagStepId, onUpdate])

  const handleConfigChange = useCallback((newConfig) => {
    onUpdate({ dagStepId: step.dagStepId, configuration: newConfig })
  }, [step.dagStepId, onUpdate])

  const handleTargetIdentifierChange = useCallback((value) => {
    onUpdate({ dagStepId: step.dagStepId, targetIdentifier: value })
  }, [step.dagStepId, onUpdate])

  const parentOptions = steps.filter(s => s.dagStepId !== step.dagStepId)

  return (
    <div>
      <div className={classes.header}>
        <h4 className={classes.title}>Edit Step</h4>
        <IconButton size='small' onClick={onClose} aria-label='close'>
          <Icon name='close' customSize='1rem' />
        </IconButton>
      </div>
      <div className={classes.body}>
        <TextField
          label='Name'
          variant='outlined'
          size='small'
          value={step.name || ''}
          onChange={(e) => handleField('name', e.target.value)}
          fullWidth
        />
        <TextField
          label='Step ID'
          variant='outlined'
          size='small'
          value={step.dagStepId || ''}
          onChange={(e) => handleField('dagStepId', e.target.value)}
          disabled={!isNew}
          helperText={isNew ? 'Unique identifier (slug format)' : 'Cannot change after creation'}
          fullWidth
        />
        <TextField
          label='Action Type'
          variant='outlined'
          size='small'
          select
          SelectProps={{ native: true }}
          value={step.targetAction || 'pass'}
          onChange={handleActionChange}
          fullWidth
        >
          {ACTION_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </TextField>
        <TextField
          label='Parent Step'
          variant='outlined'
          size='small'
          select
          SelectProps={{ native: true }}
          value={step.parentStepId || ''}
          onChange={(e) => handleField('parentStepId', e.target.value || null)}
          fullWidth
        >
          <option value=''>None (root)</option>
          {parentOptions.map(s => (
            <option key={s.dagStepId} value={s.dagStepId}>{s.name} ({s.dagStepId})</option>
          ))}
        </TextField>

        <TextField
          label='Trigger Event Type'
          variant='outlined'
          size='small'
          value={step.triggerEventType || ''}
          onChange={(e) => handleField('triggerEventType', e.target.value || null)}
          helperText='CloudEvent type that triggers this step'
          fullWidth
        />

        {step.targetAction === 'invokeLambda' ? (
          <>
            <div className={classes.divider} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!step.isAsync}
                  onChange={(e) => handleField('isAsync', e.target.checked)}
                  size='small'
                />
              }
              label='Async'
            />
            {step.isAsync ? (
              <>
                <TextField
                  label='Complete On (event type)'
                  variant='outlined'
                  size='small'
                  value={step.completeOn || ''}
                  onChange={(e) => handleField('completeOn', e.target.value || null)}
                  fullWidth
                />
                <TextField
                  label='Fail On (event type)'
                  variant='outlined'
                  size='small'
                  value={step.failOn || ''}
                  onChange={(e) => handleField('failOn', e.target.value || null)}
                  fullWidth
                />
              </>
            ) : null}
          </>
        ) : null}

        <div className={classes.divider} />

        {step.targetAction === 'start' ? (
          <StartForm
            configuration={step.configuration}
            onChange={handleConfigChange}
          />
        ) : null}

        {step.targetAction === 'invokeLambda' ? (
          <InvokeLambdaForm
            targetIdentifier={step.targetIdentifier}
            configuration={step.configuration}
            onChangeTargetIdentifier={handleTargetIdentifierChange}
            onChangeConfiguration={handleConfigChange}
          />
        ) : null}

        {step.targetAction === 'slack' ? (
          <SlackForm
            targetIdentifier={step.targetIdentifier}
            configuration={step.configuration}
            onChangeTargetIdentifier={handleTargetIdentifierChange}
            onChangeConfiguration={handleConfigChange}
          />
        ) : null}

        <div className={classes.sectionTitle}>Workflow Routing</div>
        <TextField
          label='On Complete'
          variant='outlined'
          size='small'
          value={
            step.configuration?.workflow?.onComplete === '*'
              ? '*'
              : Array.isArray(step.configuration?.workflow?.onComplete)
                ? step.configuration.workflow.onComplete.join(', ')
                : '*'
          }
          onChange={(e) => {
            const val = e.target.value.trim()
            const onComplete = val === '*' ? '*' : val.split(',').map(s => s.trim()).filter(Boolean)
            handleConfigChange({
              ...step.configuration,
              workflow: { ...(step.configuration?.workflow ?? {}), onComplete }
            })
          }}
          helperText='* for all children, or comma-separated step IDs'
          fullWidth
        />
        <TextField
          label='On Fail'
          variant='outlined'
          size='small'
          value={
            step.configuration?.workflow?.onFail === '*'
              ? '*'
              : Array.isArray(step.configuration?.workflow?.onFail)
                ? step.configuration.workflow.onFail.join(', ')
                : ''
          }
          onChange={(e) => {
            const val = e.target.value.trim()
            const onFail = val === '*' ? '*' : val.split(',').map(s => s.trim()).filter(Boolean)
            handleConfigChange({
              ...step.configuration,
              workflow: { ...(step.configuration?.workflow ?? {}), onFail }
            })
          }}
          helperText='Comma-separated step IDs, or * for all'
          fullWidth
        />
      </div>
    </div>
  )
}

StepEditorSidebar.propTypes = {
  step: PropTypes.object.isRequired,
  steps: PropTypes.array.isRequired,
  isNew: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default StepEditorSidebar
